import {
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { IconClipboardCheck } from "@tabler/icons-react";
import { usePatientStore } from "../../stores/patientStore";
import { usePatientSummary } from "../../hooks/usePatientInsights";
import { usePatientGraphPaths } from "../../hooks/usePatientGraphPaths";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useMemo, useState } from "react";

dayjs.extend(relativeTime);

const DEFAULT_TEMPLATE = `## Plan Summary

- Highlight patient-specific factors here.
- Outline dietary, activity, and monitoring guidance.
- Flag follow-up items and thresholds.`;

export function RecommendationsPage(): JSX.Element {
  const selectedPatientId = usePatientStore((state) => state.selectedPatientId);
  const { data: summary } = usePatientSummary(selectedPatientId);
  const { data: graphPaths } = usePatientGraphPaths(selectedPatientId);
  const [notes, setNotes] = useState(DEFAULT_TEMPLATE);

  const chips = useMemo(() => {
    if (!summary) return [];
    const items: string[] = [];
    if (summary.glucose.latestValue) {
      items.push(
        `Latest: ${summary.glucose.latestValue.toFixed(1)} mg/dL (${dayjs(
          summary.glucose.latestTimestamp,
        ).fromNow()})`,
      );
    }
    if (summary.lifestyle.mealCount) {
      items.push(`Meals this week: ${summary.lifestyle.mealCount}`);
    }
    if (summary.sensor.recordings) {
      items.push(`Sensor files: ${summary.sensor.recordings}`);
    }
    if (graphPaths && graphPaths.length > 0) {
      const firstPath = graphPaths[0];
      const nodeLabels = firstPath.nodes
        .map((node) => node.label)
        .filter(Boolean)
        .slice(0, 3)
        .join(" -> ");
      if (nodeLabels) {
        items.push(`Graph focus: ${nodeLabels}`);
      }
    }
    return items;
  }, [summary, graphPaths]);

  return (
    <Stack gap="xl">
      <Group justify="space-between">
        <div>
          <Title order={2}>Recommendation Console</Title>
          <Text c="dimmed">
            Translate insights into a clinician-ready plan. Notes remain local
            to this session.
          </Text>
        </div>
        <Button
          variant="light"
          leftSection={<IconClipboardCheck size={16} />}
          onClick={() => navigator.clipboard.writeText(notes)}
          disabled={!notes}
        >
          Copy Notes
        </Button>
      </Group>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="sm">
          <Text fw={600}>Snapshot</Text>
          <Group gap="xs">
            {chips.length > 0 ? (
              chips.map((chip) => (
                <Badge key={chip} variant="light" color="teal" tt="none">
                  {chip}
                </Badge>
              ))
            ) : (
              <Text size="sm" c="dimmed">
                Run an inference or view the dashboard to populate patient
                context.
              </Text>
            )}
          </Group>
        </Stack>
      </Card>

      <Textarea
        label="Clinician Notes"
        description="Draft personalized guidance, then copy into the EHR or patient report."
        minRows={16}
        value={notes}
        onChange={(event) => setNotes(event.currentTarget.value)}
      />
    </Stack>
  );
}
